import {
	subDays,
	subMonths,
	subYears,
	startOfMonth,
	endOfMonth,
	startOfYear,
	endOfYear,
	startOfDay,
	endOfDay,
} from "date-fns";

// Canonical date range presets used by every filter UI that exposes them.
// Order is fixed: Last 30 days, Last month, YTD, Last year.
export const DATE_PRESET_LABELS = [
	"Last 30 days",
	"Last month",
	"YTD",
	"Last year",
] as const;

export type DatePresetLabel = (typeof DATE_PRESET_LABELS)[number];

const buildRange = (label: DatePresetLabel): { from: Date; to: Date } => {
	const now = new Date();
	switch (label) {
		case "Last 30 days":
			return { from: startOfDay(subDays(now, 30)), to: endOfDay(now) };
		case "Last month":
			return {
				from: startOfMonth(subMonths(now, 1)),
				to: endOfMonth(subMonths(now, 1)),
			};
		case "YTD":
			return { from: startOfYear(now), to: endOfDay(now) };
		case "Last year":
			return {
				from: startOfYear(subYears(now, 1)),
				to: endOfYear(subYears(now, 1)),
			};
		default: {
			// Exhaustiveness guard: adding a new entry to DATE_PRESET_LABELS
			// without extending this switch becomes a compile-time error here.
			const _exhaustive: never = label;
			throw new Error(`Unhandled date preset: ${_exhaustive}`);
		}
	}
};

// Eager Date-based presets — matches @hotelise/ui DateRangePreset
// (used by DashboardFilter, ExpensesFilter)
export const getDatePresets = (): Array<{ label: DatePresetLabel; from: Date; to: Date }> =>
	DATE_PRESET_LABELS.map((label) => ({ label, ...buildRange(label) }));

// Lazy Date-based presets — range computed at click time
// (used by GuestsPage)
export const DATE_PRESETS: Array<{
	label: DatePresetLabel;
	getRange: () => { from: Date; to: Date };
}> = DATE_PRESET_LABELS.map((label) => ({
	label,
	getRange: () => buildRange(label),
}));

// Lazy ISO-string presets — for handlers that store ISO strings
// (used by FiltersPanel, StatementsPage)
export const DATE_PRESETS_ISO: Array<{
	label: DatePresetLabel;
	getRange: () => { from: string; to: string };
}> = DATE_PRESET_LABELS.map((label) => ({
	label,
	getRange: () => {
		const { from, to } = buildRange(label);
		return { from: from.toISOString(), to: to.toISOString() };
	},
}));
